import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlignLeft, CalendarDays, Mail, MessageCircle, Package, TriangleAlert, User, Users } from 'lucide-react'
import { usePublicContent } from '../publicContent/usePublicContent'
import LegalFooter from './LegalFooter'
import './LegalPage.css'
import './PrivacyPage.css'

const TOC_IDS = ['section-1', 'section-2', 'section-3', 'section-4', 'section-5']
const KIND_ICONS = [User, Package, Users]
const PURPOSE_COLORS = ['accent', 'green', 'blue']

/** Protection des données marchandes (maquette study/web/protection_donnees). Texte provisoire : les passages
 *  [À compléter] sont à renseigner avant publication. Textes dans la langue du site. */
export default function PrivacyPage() {
  const { c } = usePublicContent()
  const p = c.privacy

  useEffect(() => {
    window.scrollTo(0, 0)
    const previous = document.title
    document.title = `${p.docTitle} · Boutiklic`
    return () => {
      document.title = previous
    }
  }, [p.docTitle])

  return (
    <div className="privacy-page">
      <header className="legal-top">
        <div className="pp-container legal-top-inner">
          <Link to="/" className="legal-brand">Boutiklic</Link>
          <Link to="/" className="legal-back">{c.common.backHome}</Link>
        </div>
      </header>

      <main className="pp-main">
        <div className="pp-wrap">
          <section className="pp-hero" aria-labelledby="pp-title">
            <div className="pp-eyebrow">
              <span className="pp-dot" />
              {p.eyebrow}
            </div>
            <h1 id="pp-title">{p.docTitle}<span>.</span></h1>
            <p className="pp-intro">{p.intro}</p>
            <div className="pp-updated">
              <CalendarDays size={16} />
              <span>{c.common.updatedPrefix} {c.common.updatedAt}</span>
            </div>
            <div className="pp-alert" role="status">
              <span><TriangleAlert size={16} /></span>
              <p>{c.common.draftBanner}</p>
            </div>
          </section>

          <section className="pp-toc" aria-label={p.tocAria}>
            <div>
              <div className="pp-toc-title">
                <AlignLeft size={16} />
                <span>{p.tocTitle}</span>
              </div>
              <nav>
                {TOC_IDS.map((id, i) => (
                  <a key={id} href={`#${id}`}>
                    <span>{String(i + 1).padStart(2, '0')}</span>
                    <span>{p.toc[i]}</span>
                  </a>
                ))}
              </nav>
            </div>
          </section>

          <div className="pp-sections">
            <Article id="section-1" n={1} title={p.s1Title}>
              <p className="pp-lead">{p.s1Lead}</p>
              <div className="pp-kinds">
                {p.kinds.map((k, i) => {
                  const Icon = KIND_ICONS[i]
                  return (
                    <div key={k.title} className="pp-kind">
                      <span className={`pp-kind-icon${i === 0 ? ' pp-kind-icon-accent' : ''}`}><Icon size={16} /></span>
                      <h3>{k.title}</h3>
                      <p>{k.text}</p>
                    </div>
                  )
                })}
              </div>
            </Article>

            <Article id="section-2" n={2} title={p.s2Title}>
              <p className="pp-lead">{p.s2Lead}</p>
              <div className="pp-purposes">
                {p.purposes.map((pu, i) => (
                  <div key={pu.title}>
                    <h3><span className={`pp-bullet pp-bullet-${PURPOSE_COLORS[i]}`} />{pu.title}</h3>
                    <p>{pu.text}</p>
                  </div>
                ))}
              </div>
            </Article>

            <Article id="section-3" n={3} title={p.s3Title}>
              <p className="pp-lead">{p.s3Lead}</p>
              <div className="pp-subs">
                <div className="pp-sub">
                  <div className="pp-sub-main">
                    <span className="pp-sub-badge pp-sub-badge-blue">OVH</span>
                    <div>
                      <h4>{p.subHostTitle}</h4>
                      <p>{p.subHostText}</p>
                    </div>
                  </div>
                  <span className="pp-tag pp-tag-green">{p.subHostTag}</span>
                </div>
                <div className="pp-sub">
                  <div className="pp-sub-main">
                    <span className="pp-sub-badge pp-sub-badge-gray"><MessageCircle size={16} /></span>
                    <div>
                      <h4>{p.subSmsTitle}</h4>
                      <p>{p.subSmsText}</p>
                    </div>
                  </div>
                  <span className="pp-tag pp-tag-gray">{p.subSmsTag}</span>
                </div>
                <div className="pp-sub pp-sub-todo">
                  <div className="pp-sub-main">
                    <span className="pp-sub-badge pp-sub-badge-amber">[…]</span>
                    <div>
                      <h4>{p.subFilesTitle}</h4>
                      <p className="pp-mono">{c.common.todo}</p>
                    </div>
                  </div>
                  <span className="pp-tag pp-tag-amber">{p.subFilesTag}</span>
                </div>
              </div>
            </Article>

            <Article id="section-4" n={4} title={p.s4Title}>
              <p className="pp-lead pp-lead-tight">{p.s4Lead} <Todo label={c.common.todo} />.</p>
              <div className="pp-box">
                <p className="pp-box-title">{p.boxTitle}</p>
                <ul>
                  {p.boxItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </Article>

            <Article id="section-5" n={5} title={p.s5Title}>
              <p className="pp-lead">{p.s5Lead} <Todo label={c.common.todo} />.</p>
              <div className="pp-rights">
                {p.rights.map((r) => (
                  <div key={r.title}>
                    <strong>{r.title}</strong>
                    <span>{r.text}</span>
                  </div>
                ))}
              </div>
              <div className="pp-channel">
                <div className="pp-channel-main">
                  <span><Mail size={16} /></span>
                  <div>
                    <p>{p.channelLabel}</p>
                    <p className="pp-channel-value">{c.common.todo}</p>
                  </div>
                </div>
                <Link to="/contact" className="pp-btn-accent">{p.channelButton}</Link>
              </div>
            </Article>
          </div>

          <section className="pp-cta">
            <div className="pp-glow-top" />
            <div className="pp-glow-bottom" />
            <div className="pp-cta-inner">
              <div className="pp-cta-tag">
                <MessageCircle size={14} />
                {p.ctaTag}
              </div>
              <h3>{p.ctaTitle}</h3>
              <p>{p.ctaText}</p>
              <Link to="/contact" className="pp-btn-white">{p.ctaButton}</Link>
            </div>
          </section>
        </div>
      </main>

      <LegalFooter current="privacy" />
    </div>
  )
}

function Article({ id, n, title, children }: { id: string; n: number; title: string; children: ReactNode }) {
  return (
    <article className="pp-card" id={id}>
      <div className="pp-card-head">
        <span>{n}</span>
        <h2>{n}. {title}</h2>
      </div>
      {children}
    </article>
  )
}

function Todo({ label }: { label: string }) {
  return <span className="pp-todo">{label}</span>
}
